import React from "react";
import {addMonths, format, isSameDay, lastDayOfMonth} from "date-fns";
import {capitalize, classNames, getLocale, gotoNextSunday, gotoPreviousMonday, monthOfWeeks} from "./shared/helpers";
import "./AvailabilitySearchCalendar.scss";
import {DateAvailability} from "./shared/graphql-types.generated";
import Spinner2 from "./Spinner2";
import {AvailabilitySearchCalendarTranslations} from "./translations/AvailabilitySearchCalendarTranslations";


export default function AvailabilitySearchCalendar({
    translations,
    month,
    year,
    currentDate,
    onMonthChanged,
    onCurrentDateChanged,
    daysWithAvailability,
    loading
}:{
    durations: number[],
    adults: number,
    children: number,
    infants: number,
    translations: AvailabilitySearchCalendarTranslations,
    month: number,
    year: number,
    currentDate: Date,
    onMonthChanged: (month: number, year: number) => void
    onCurrentDateChanged: (date: Date) => void,
    daysWithAvailability: DateAvailability[],
    loading: boolean
}) {
    
    // The month uses 1 for January, 2 for February, etc.
    const monthStartDate = new Date(year, month - 1, 1);
    const monthEndDate = lastDayOfMonth(monthStartDate);
    const calendarStartDate = gotoPreviousMonday(monthStartDate);
    const calendarEndDate = gotoNextSunday(monthEndDate);

    const isInThePast = (date: Date) => date < new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
    const isOtherMonth = (date: Date) => date < monthStartDate || date > monthEndDate;
    const isCurrentDate = (date: Date) => isSameDay(date, currentDate);
    const isDayWithAvailability = (date: Date) => daysWithAvailability.some(d => isSameDay(d.date, date) && d.availability > 0);
    const isDayWithNoAvailability = (date: Date) => daysWithAvailability.some(d => isSameDay(d.date, date) && d.availability === 0);
    
    const canGotoPreviousMonth = calendarStartDate > new Date();
    
    const handleGotoPreviousMonth = () => {
        if (canGotoPreviousMonth) {
            const newMonthStartDate = addMonths(monthStartDate, -1);
            onMonthChanged(newMonthStartDate.getMonth() + 1, newMonthStartDate.getFullYear());
        }
    }

    const handleGotoNextMonth = () => {
        const newMonthStartDate = addMonths(monthStartDate, 1);
        onMonthChanged(newMonthStartDate.getMonth() + 1, newMonthStartDate.getFullYear());
    }

    return (
        <div className="bwp-availability-search-calendar">
            <div className="bwp-availability-search-calendar__header">
                <div>
                    <button type="button" disabled={!canGotoPreviousMonth} title="Gå til forrige måned" onClick={handleGotoPreviousMonth}>
                        <svg xmlns="http://www.w3.org/2000/svg"
                             viewBox="0 0 256 512">
                            <path
                                d="M9.4 278.6c-12.5-12.5-12.5-32.8 0-45.3l128-128c9.2-9.2 22.9-11.9 34.9-6.9s19.8 16.6 19.8 29.6l0 256c0 12.9-7.8 24.6-19.8 29.6s-25.7 2.2-34.9-6.9l-128-128z"/>
                        </svg>
                    </button>
                </div>
                <div className="bwp-title">{capitalize(format(monthStartDate, "MMMM yyyy", { locale: getLocale(window["BookingStudio_culture"]) }))}</div>
                <div>
                    <button type="button" title="Gå til næste måned" onClick={handleGotoNextMonth}>
                        <svg xmlns="http://www.w3.org/2000/svg"
                             viewBox="0 0 256 512">
                            <path
                                d="M246.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-128-128c-9.2-9.2-22.9-11.9-34.9-6.9s-19.8 16.6-19.8 29.6l0 256c0 12.9 7.8 24.6 19.8 29.6s25.7 2.2 34.9-6.9l128-128z"/>
                        </svg>
                    </button>
                </div>
            </div>
            <div className="bwp-availability-search-calendar__weekdays">
                <div title={translations.monday}>{translations.monday.charAt(0).toUpperCase()}</div>
                <div title={translations.tuesday}>{translations.tuesday.charAt(0).toUpperCase()}</div>
                <div title={translations.wednesday}>{translations.wednesday.charAt(0).toUpperCase()}</div>
                <div title={translations.thursday}>{translations.thursday.charAt(0).toUpperCase()}</div>
                <div title={translations.friday}>{translations.friday.charAt(0).toUpperCase()}</div>
                <div title={translations.saturday}>{translations.saturday.charAt(0).toUpperCase()}</div>
                <div title={translations.sunday}>{translations.sunday.charAt(0).toUpperCase()}</div>
            </div>
            <div className="bwp-availability-search-calendar__body">
                {loading && (
                    <div className="bwp-loading"><Spinner2 width="64px" height="64px" /></div>
                )}
                {[...monthOfWeeks(calendarStartDate, calendarEndDate)].map((week, i) => (
                    <div key={`week-${i}`} className="bwp-availability-search-calendar__week">
                        {[...week].map((day, j) => (
                            <div key={`day-${j}`} className={classNames(["bwp-availability-search-calendar__day", {
                                "bwp-past-in-month": isInThePast(day),
                                "bwp-other-month": isOtherMonth(day),
                                "bwp-current-date": isCurrentDate(day),
                                "bwp-day-with-no-availability": isDayWithNoAvailability(day),
                                "bwp-day-with-availability": isDayWithAvailability(day),
                            }])}>
                                {isDayWithAvailability(day) ? (
                                    <button onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onCurrentDateChanged(day)
                                    }}>{day.getDate()}</button>
                                ) : (
                                    <span>{day.getDate()}</span>
                                )}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
}